import React, { useState, useEffect } from "react";
import "./FeedItem.css";
import CommentBottomSheet from "./CommentBottomSheet";
import ReportBottomSheet from "./ReportBottomSheet";
import FeedMediaSlider from "./FeedMediaSlider";
import FeedCommentInput from "./FeedCommentInput";
import HeartBurstAnimation from "./HeartBurstAnimation";
import { useAuth } from "../context/AuthContext";
import {
  getProfileImageWithFallback,
  handleImageError,
  getProfileImageUrl,
} from "../utils/imageUtils";
import {
  Comment as FirebaseComment,
  addComment,
  listenToComments,
  toggleCommentLike,
  toggleFeedLike,
  getFeedLikeInfo,
} from "../firebase/firebase";

export interface Comment {
  id: string;
  profileImage: string;
  nickname: string;
  content: string;
  createdAt: string;
  likes?: number;
  liked?: boolean;
  parentCommentId?: string;
}

export interface MediaFile {
  url: string;
  type: string; // "image" 또는 "video"
  name?: string;
}

export interface FeedItemProps {
  id: string;
  profileImage: string;
  nickname: string;
  userType: string;
  petInfo: string;
  content: string;
  mediaFiles: MediaFile[];
  tags?: string[];
  postedAt: string;
  comments: Comment[];
  initialLikeCount?: number;
  initialIsLiked?: boolean;
}

const FeedItem: React.FC<FeedItemProps> = ({
  id,
  profileImage,
  nickname,
  userType,
  petInfo,
  content,
  mediaFiles,
  tags,
  postedAt,
  comments: initialComments = [],
  initialLikeCount = 0,
  initialIsLiked = false,
}) => {
  const [isCommentSheetOpen, setIsCommentSheetOpen] = useState(false);
  const [isReportSheetOpen, setIsReportSheetOpen] = useState(false);
  const [comments, setComments] = useState<FirebaseComment[]>([]);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [isLiking, setIsLiking] = useState(false);
  const [showHeartBurst, setShowHeartBurst] = useState(false);
  const { currentUser, userProfile } = useAuth();

  // 댓글 실시간 리스너 설정
  useEffect(() => {
    const unsubscribe = listenToComments(id, (fetchedComments) => {
      setComments(fetchedComments);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [id]);

  // 좋아요 정보 로드
  useEffect(() => {
    const loadLikeInfo = async () => {
      try {
        const { likeCount: fetchedLikeCount, isLiked: fetchedIsLiked } =
          await getFeedLikeInfo(id, currentUser?.uid);
        setLikeCount(fetchedLikeCount);
        setIsLiked(fetchedIsLiked);
      } catch (error) {
        console.error("좋아요 정보 로드 오류:", error);
      }
    };

    if (currentUser) {
      loadLikeInfo();
    }
  }, [id, currentUser?.uid]);

  const handleCommentSubmit = async (
    comment: string,
    parentCommentId?: string
  ) => {
    if (!currentUser || !userProfile) {
      alert("로그인이 필요합니다.");
      return;
    }

    if (isSubmittingComment) return;

    try {
      setIsSubmittingComment(true);
      await addComment(
        id,
        currentUser.uid,
        userProfile.name || "익명",
        getProfileImageWithFallback(userProfile.profileImage),
        comment,
        parentCommentId
      );
    } catch (error) {
      console.error("댓글 작성 중 오류:", error);
      alert("댓글 작성에 실패했습니다.");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCommentLike = async (commentId: string) => {
    if (!currentUser) {
      alert("로그인이 필요합니다.");
      return;
    }

    try {
      await toggleCommentLike(id, commentId, currentUser.uid);
    } catch (error) {
      console.error("댓글 좋아요 처리 중 오류:", error);
      alert("좋아요 처리에 실패했습니다.");
    }
  };

  const handleFeedLike = async () => {
    if (!currentUser) {
      alert("로그인이 필요합니다.");
      return;
    }

    if (isLiking) return;

    try {
      setIsLiking(true);

      // 낙관적 업데이트
      const newIsLiked = !isLiked;
      setIsLiked(newIsLiked);
      setLikeCount((prev) => (newIsLiked ? prev + 1 : Math.max(0, prev - 1)));

      // 좋아요를 누른 경우에만 애니메이션 표시
      if (newIsLiked) {
        setShowHeartBurst(true);
      }

      await toggleFeedLike(id, currentUser.uid);
    } catch (error) {
      console.error("피드 좋아요 처리 중 오류:", error);
      alert("좋아요 처리에 실패했습니다.");

      // 오류 시 상태 롤백
      setIsLiked(!isLiked);
      setLikeCount((prev) => (isLiked ? prev + 1 : Math.max(0, prev - 1)));
    } finally {
      setIsLiking(false);
    }
  };

  const handleReport = (reason: string) => {
    console.log("Report submitted:", { feedId: id, reason });
    // TODO: API 호출 구현
  };

  // FirebaseComment를 기존 Comment 형식으로 변환
  const convertedComments: Comment[] = comments.map(
    (comment) =>
      ({
        id: comment.id,
        profileImage: getProfileImageWithFallback(comment.userProfileImage),
        nickname: comment.userNickname,
        content: comment.content,
        createdAt: new Date(comment.createdAt).toLocaleString("ko-KR", {
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        }),
        likes: comment.likes || 0,
        liked:
          currentUser && comment.likedUsers
            ? comment.likedUsers[currentUser.uid] === true
            : false,
        ...(comment.parentCommentId && {
          parentCommentId: comment.parentCommentId,
        }),
      } as any)
  );

  return (
    <div className="feed-item">
      <div className="feed-item-header">
        <div className="feed-item-user-info">
          <img
            src={getProfileImageUrl(profileImage, "medium")}
            alt={nickname}
            className="feed-item-profile-image"
            loading="lazy"
            onError={handleImageError}
          />
          <div className="feed-item-user-details">
            <div className="feed-item-name-container">
              <h3 className="feed-item-user-name">{nickname}</h3>
              <span className="feed-item-user-type">{userType}</span>
            </div>
            <p className="feed-item-pet-info">{petInfo}</p>
          </div>
        </div>
        <div className="feed-item-header-actions">
          {/* <button
            className="feed-item-more-button"
            onClick={() => setIsReportSheetOpen(true)}
          >
            신고하기
          </button> */}
        </div>
      </div>

      <div className="feed-item-content">
        {mediaFiles.length > 0 && <FeedMediaSlider mediaFiles={mediaFiles} />}

        <div className="feed-item-actions">
          <button
            className={`feed-item-action-button ${isLiked ? "liked" : ""}`}
            onClick={handleFeedLike}
            disabled={isLiking}
            style={{ position: "relative" }}
          >
            <div className="feed-like-container">
              <img
                src={
                  isLiked
                    ? `${process.env.PUBLIC_URL}/images/icons/feed-like-active.png`
                    : `${process.env.PUBLIC_URL}/images/icons/feed-like.png`
                }
                alt="좋아요"
                className="feed-like-icon"
              />
              {likeCount > 0 && (
                <span className="feed-like-count">{likeCount}</span>
              )}
            </div>
            <HeartBurstAnimation
              isVisible={showHeartBurst}
              onAnimationEnd={() => setShowHeartBurst(false)}
            />
          </button>
          <button
            className="feed-item-action-button"
            onClick={() => setIsCommentSheetOpen(true)}
          >
            <div className="feed-comment-container">
              <img
                src={`${process.env.PUBLIC_URL}/images/icons/feed-reply.svg`}
                alt="댓글"
                className="feed-comment-icon"
              />
              {comments.length > 0 && (
                <span className="feed-comment-count">{comments.length}</span>
              )}
            </div>
          </button>
          <button className="feed-item-action-button">
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/feed-share.svg`}
              alt="공유"
            />
          </button>
        </div>

        <p>{content}</p>
        <div className="feed-item-tags">
          {tags &&
            tags.length > 0 &&
            tags.map((tag, index) => (
              <span key={index} className="feed-item-tag">
                #{tag}
              </span>
            ))}
        </div>

        <span className="feed-item-posted-time">{postedAt}</span>
      </div>

      <div className="feed-item-footer">
        <FeedCommentInput onSubmit={handleCommentSubmit} />
      </div>

      <CommentBottomSheet
        isOpen={isCommentSheetOpen}
        onClose={() => setIsCommentSheetOpen(false)}
        comments={convertedComments}
        onSubmitComment={handleCommentSubmit}
        onLikeComment={handleCommentLike}
      />

      <ReportBottomSheet
        isOpen={isReportSheetOpen}
        onClose={() => setIsReportSheetOpen(false)}
        onReport={handleReport}
      />
    </div>
  );
};

export default FeedItem;
